import React, { useState, useImperativeHandle, forwardRef } from "react";
import { cn } from "../../utils/cn";
import { AlertCircle, CheckCircle2, Info, AlertTriangle, X } from "lucide-react";

export const Toast = forwardRef(({ position = "top-right", className }, ref) => {
  const [toasts, setToasts] = useState([]);

  useImperativeHandle(ref, () => ({
    show: (toast) => {
      const items = Array.isArray(toast) ? toast : [toast];
      const withIds = items.map((item) => ({
        ...item,
        id: item.id || Math.random().toString(36).substring(2, 9),
      }));

      setToasts((prev) => [...prev, ...withIds]);

      withIds.forEach((item) => {
        if (!item.sticky) {
          const timeout = item.life || 3500;
          setTimeout(() => {
            setToasts((curr) => curr.filter((t) => t.id !== item.id));
          }, timeout);
        }
      });
    },
    clear: () => {
      setToasts([]);
    },
    replace: (toast) => {
      const items = Array.isArray(toast) ? toast : [toast];
      const withIds = items.map((item) => ({
        ...item,
        id: item.id || Math.random().toString(36).substring(2, 9),
      }));
      setToasts(withIds);
    }
  }));

  const positionClasses = {
    "top-right": "top-5 right-5",
    "top-left": "top-5 left-5",
    "top-center": "top-5 left-1/2 -translate-x-1/2",
    "bottom-right": "bottom-5 right-5",
    "bottom-left": "bottom-5 left-5",
    "bottom-center": "bottom-5 left-1/2 -translate-x-1/2",
  };

  const severityConfigs = {
    info: {
      bg: "bg-slate-900 border-blue-500/40 text-blue-200",
      icon: Info,
      iconColor: "text-blue-400",
    },
    success: {
      bg: "bg-slate-900 border-urb-primary/40 text-emerald-200",
      icon: CheckCircle2,
      iconColor: "text-urb-primary",
    },
    warn: {
      bg: "bg-slate-900 border-amber-500/40 text-amber-200",
      icon: AlertTriangle,
      iconColor: "text-amber-400",
    },
    warning: {
      bg: "bg-slate-900 border-amber-500/40 text-amber-200",
      icon: AlertTriangle,
      iconColor: "text-amber-400",
    },
    error: {
      bg: "bg-slate-900 border-red-500/40 text-red-200",
      icon: AlertCircle,
      iconColor: "text-red-400",
    },
  };

  if (toasts.length === 0) return null;

  return (
    <div
      className={cn(
        "fixed z-50 flex flex-col gap-2.5 max-w-sm w-full pointer-events-none px-4 sm:px-0",
        positionClasses[position] || positionClasses["top-right"],
        className
      )}
    >
      {toasts.map((t) => {
        const config = severityConfigs[t.severity] || severityConfigs.info;
        const IconComponent = config.icon;

        return (
          <div
            key={t.id}
            className={cn(
              "pointer-events-auto flex items-start gap-3 p-4 rounded-xl border shadow-2xl backdrop-blur-md transition-all duration-300 animate-in fade-in slide-in-from-top-2",
              config.bg
            )}
          >
            <IconComponent className={cn("w-5 h-5 shrink-0 mt-0.5", config.iconColor)} />
            <div className="flex-1 min-w-0">
              {t.summary && (
                <h6 className="font-semibold text-white text-sm leading-tight mb-0.5">
                  {t.summary}
                </h6>
              )}
              {t.detail && (
                <div className="text-xs text-slate-300 leading-relaxed break-words">
                  {t.detail}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setToasts((curr) => curr.filter((item) => item.id !== t.id))}
              className="text-slate-400 hover:text-white p-1 rounded-md transition-colors bg-transparent border-none cursor-pointer -mr-1 -mt-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
});

Toast.displayName = "Toast";
export default Toast;
